import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './PosterCreateExact.css';
import UserHeader from './UserHeader';
import { listPosterImages } from '../firebase/storage';
import { useAuth } from '../firebase/AuthContext';
import { trackPosterCreation } from '../utils/analytics';

const PosterCreate = () => {
  const carouselRef = useRef(null);
  const { userProfile, logout, sessionId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const handleEditProfile = () => {
    navigate('/profile-setup', { state: { editMode: true } });
  };

  // 支払いステータスをチェックする関数
  const checkPremiumStatus = async () => {
    try {
      if (!sessionId) return false;

      const { checkPaymentStatus } = await import('../firebase/api');
      const result = await checkPaymentStatus(sessionId);
      setHasPremiumAccess(result.is_paid);
      // Premium status checked
      return result.is_paid;
    } catch (error) {
      console.error('Premium status check failed:', error);
      setHasPremiumAccess(false);
      return false;
    }
  };

  // ユーザーのポスター数をチェックする関数
  const checkUserPosterCount = async () => {
    try {
      if (!sessionId) return 0;

      const { getUserPosterCount } = await import('../firebase/api');
      const count = await getUserPosterCount(sessionId);
      return count;
    } catch (error) {
      console.error('Failed to get user poster count:', error);
      return 0;
    }
  };

  // 課金UIを表示（Stripe Checkout）
  const showPaymentUI = async () => {
    try {
      if (!sessionId) {
        alert('認証情報がありません。再ログインしてください。');
        return;
      }
      const { createCheckoutSession } = await import('../firebase/api');
      
      const checkoutData = await createCheckoutSession(sessionId, {
        userId: userProfile?.uid,
        successUrl: `${window.location.origin}/payment-success?session_id={CHECKOUT_SESSION_ID}&redirect_to=poster-create`,
        cancelUrl: `${window.location.origin}/poster-create`
      });
      
      // Stripe.jsを読み込んでCheckoutページにリダイレクト
      const { loadStripe } = await import('@stripe/stripe-js');
      const stripe = await loadStripe('pk_live_51S770FRyfyfP7e06hbcyWAb7pQbz5yl41e9RnmrYALA4wz7FxHUMvFHfRX9Y9lpGgznKYj5Q8XTEjXbMkzRFiklr006Rvh4Qs6');
      
      const result = await stripe.redirectToCheckout({
        sessionId: checkoutData.sessionId
      });
      
      if (result.error) {
        console.error('Checkout error:', result.error);
        alert('決済の開始に失敗しました');
      }
    } catch (error) {
      console.error('Payment setup error:', error);
      alert('決済システムの読み込みに失敗しました');
    }
  };

  // コンテンツ警告モーダルを表示
  const showContentWarning = (message, onContinue) => {
    setContentWarningData({ message, callback: onContinue });
    setShowContentWarningModal(true);
  };

  // コンテンツ警告モーダルの処理
  const handleContentWarningContinue = () => {
    setShowContentWarningModal(false);
    if (contentWarningData.callback) {
      contentWarningData.callback();
    }
    setContentWarningData({ message: '', callback: null });
  };

  const handleContentWarningCancel = () => {
    setShowContentWarningModal(false);
    setContentWarningData({ message: '', callback: null });
  };

  // プレビュー生成の続行処理
  const continueGeneratePreview = async () => {
    try {
      // セッションID取得
      if (!sessionId) {
        alert('認証情報がありません。再ログインしてください。');
        setIsGenerating(false);
        return;
      }

      // API.js経由でバックエンド呼び出し
      const { generatePosterPreview } = await import('../firebase/api');
      const payload = {
        posterId: selectedPoster.id,
        posterPath: selectedPoster.path,
        textLines: textLines.slice(0, 3),
        font: selectedFont
      };
      const data = await generatePosterPreview(sessionId, payload);
      setGeneratedPreviews(data.previews);
      setShowPreviews(true);
      setIsGenerating(false);
    } catch (error) {
      console.error('プレビュー生成エラー:', error);
      alert('プレビューの生成に失敗しました');
      setIsGenerating(false);
    }
  };

  // URLパラメータやlocation stateから質問情報を取得
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // 編集モード用ポスター情報
  const [editPosterInfo, setEditPosterInfo] = useState(null); // { imageUrl, title }

  // 支払いステータス管理
  const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showContentWarningModal, setShowContentWarningModal] = useState(false);
  const [contentWarningData, setContentWarningData] = useState({ message: '', callback: null });

  // フォーム状態
  const [selectedPoster, setSelectedPoster] = useState(null); // null初期値に変更
  const [textLines, setTextLines] = useState(['', '', '']);
  const [selectedFont, setSelectedFont] = useState('YDWbananaslipplus');
  const [isPublic, setIsPublic] = useState(false);

  // プレビュー状態
  const [generatedPreviews, setGeneratedPreviews] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreviews, setShowPreviews] = useState(false);

  // 利用可能なフォントリスト
  const [availableFonts] = useState([
    { id: 'Pigmo-00', name: 'Pigmo-00', family: 'Pigmo-00' },
    { id: 'gomarice_mukasi_mukasi', name: 'ごまりんぐ昔々', family: 'gomarice_mukasi_mukasi' },
    { id: 'YDWbananaslipplus', name: 'バナナスリップ', family: 'YDWbananaslipplus' },
    { id: 'HiraginoMincho', name: 'ヒラギノ明朝 ProN', family: 'HiraginoMincho' },
    { id: 'HiraginoSans', name: 'ヒラギノ角ゴシック W4', family: 'HiraginoSans' },
    { id: 'keifont', name: 'けいふぉんと！', family: 'keifont' },
    { id: 'crayon_1-1', name: '殴り書きクレヨン', family: 'crayon_1-1' },
    { id: 'ZeroGothic', name: '零ゴシック', family: 'ZeroGothic' },
    { id: 'yoppa', name: 'よっぱふで', family: 'yoppa' }
  ]);

  // ポスター背景リスト（実際はDBから取得）
  const [posters, setPosters] = useState([]);
  // Storageからポスター画像一覧を取得
  useEffect(() => {
    const fetchPosters = async () => {
      const images = await listPosterImages();
      // idを付与
      setPosters(images.map((img, idx) => ({ id: idx + 1, name: img.name, path: img.url })));
    };
    fetchPosters();
  }, []);

  useEffect(() => {
    // デフォルトで最初のポスターを選択
    if (posters.length > 0 && !selectedPoster) {
      setSelectedPoster(posters[0]);
    }
  }, [posters, selectedPoster]);

  useEffect(() => {
    // Top.jsから渡された質問情報を取得
    if (location.state?.question) {
      setSelectedQuestion({
        id: location.state.question.id,
        title: location.state.question.title,
      });
    } else {
      setSelectedQuestion({ id: '', title: '' });
    }

    // 編集モード時はgetPosterInfoで画像とtitleを取得
    if (location.state?.posterId) {
      const fetchEditPosterInfo = async () => {
        if (!sessionId) {
          setEditPosterInfo(null);
          return;
        }
        try {
          const { getPosterInfo } = await import('../firebase/api');
          const info = await getPosterInfo(sessionId, location.state.posterId);
          // 編集モード用ポスター情報取得
          setEditPosterInfo({ imageUrl: info.imageUrl, title: info.title });
          // 1行目テキスト初期値セット（未設定時のみ）
          setTextLines(prev => [info.title, info.title2, info.title3 || prev[0], prev[1], prev[2]]);
        } catch (err) {
          setEditPosterInfo(null);
        }
      };
      fetchEditPosterInfo();
    } else {
      setEditPosterInfo(null);
    }

    // ポスター数と課金状況をチェック（編集モードでない場合のみ）
    if (!location.state?.posterId) {
      const checkPosterLimits = async () => {
        const isPremium = await checkPremiumStatus();
        const posterCount = await checkUserPosterCount();
        if (posterCount >= 6 && !isPremium) {
          setShowPaymentModal(true);
        }
      };
      
      checkPosterLimits();
    }
  }, [location.state, sessionId]);

  // カルーセルナビゲーション（自動スクロール防止機能付き）
  const scrollCarousel = (direction) => {
    const scrollAmount = 200; // スクロール量を大きく調整（例: 200px）
    
    // scrollCarousel called
    
    if (carouselRef.current) {
      // Before scroll
      
      // スクロール許可フラグを設定
      carouselRef.current.dataset.allowScroll = 'true';
      
      // ボタンクリック時のみスムーズスクロールを適用
      if (direction === 'next') {
        carouselRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      } else if (direction === 'prev') {
        carouselRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      }
      
      // アニメーション完了後にフラグをリセット
      setTimeout(() => {
        if (carouselRef.current) {
          carouselRef.current.dataset.allowScroll = 'false';
          // After scroll completed
        }
      }, 500);
    }
  };
  
  // トグルスイッチ変更（DOM状態と同期）
  const handleToggleChange = async (e) => {
    const newValue = e.target.checked;
    
    // 公開設定をONにする場合のみコンテンツチェック
    if (newValue) {
      try {
        if (sessionId) {
          const { moderateContent } = await import('../firebase/api');
          const moderationResult = await moderateContent(sessionId, textLines);
          
          if (!moderationResult.allowed) {
            showContentWarning(
              `公開できません: ${moderationResult.message}`,
              null // 続行オプションなし（OKボタンのみ）
            );
            // チェックボックスを元に戻す
            e.target.checked = false;
            return;
          }
        }
      } catch (error) {
        console.error('コンテンツモデレーションエラー:', error);
        // エラー時は警告を出すが続行
        showContentWarning(
          'コンテンツチェックでエラーが発生しました。公開を続行しますか？',
          () => {
            setIsPublic(newValue);
            // Toggle changed on error continue
          }
        );
        e.target.checked = false;
        return;
      }
    }
    
    setIsPublic(newValue);
    // Toggle changed
  };

  // ポスター選択ハンドラー
  const handlePosterSelect = (poster) => {
    setSelectedPoster(poster);
    // ポスター選択完了
  };

  // フォント選択ハンドラー
  const handleFontSelect = (font) => {
    setSelectedFont(font.id);
    // フォント選択完了
  };

  // 選択されたフォントの詳細を取得
  const getSelectedFontDetails = () => {
    return availableFonts.find(font => font.id === selectedFont) || availableFonts[5]; // デフォルトはバナナスリップ
  };

  // イメージ生成
  const handleGeneratePreview = async () => {
    if (!selectedPoster || textLines.slice(0, 3).every(line => !line.trim())) {
      alert('ポスターと文言を選択してください');
      return;
    }

    // コンテンツモデレーションチェック（公開設定がONの場合のみ）
    if (isPublic) {
      try {
        if (sessionId) {
          const { moderateContent } = await import('../firebase/api');
          const moderationResult = await moderateContent(sessionId, textLines.slice(0, 3));
          
          if (!moderationResult.allowed) {
            showContentWarning(
              `不適切な内容が含まれています: ${moderationResult.message}\n公開設定を無効にして続行するか、内容を修正してください。`,
              null // 続行オプションなし（OKボタンのみ）
            );
            return;
          }
        }
      } catch (error) {
        console.error('コンテンツモデレーションエラー:', error);
        showContentWarning(
          'コンテンツチェックでエラーが発生しました。続行しますか？',
          () => {
            setIsGenerating(true);
            setShowPreviews(false);
            // 後続の処理を続行
            continueGeneratePreview();
          }
        );
        return;
      }
    }

    setIsGenerating(true);
    setShowPreviews(false);
    continueGeneratePreview();
  };

  // 最終決定
  const [loading, setLoading] = useState(false); // Add loading state
  const handleFinalSelect = async (previewId) => {
    setLoading(true); // Show loading spinner
    try {
      // 選択されたプレビューの情報を取得
      const selectedPreview = generatedPreviews.find(preview => preview.id === previewId);
      if (!selectedPreview) {
        alert('プレビュー画像が見つかりません');
        return;
      }
      if (!sessionId) {
        alert('認証情報がありません。再ログインしてください。');
        return;
      }
      // API.js経由でバックエンド呼び出し
      const { createPoster } = await import('../firebase/api');
      const payload = {
        previewId: selectedPreview.id,
        previewPath: selectedPreview.imageUrl, // preview/poster/... のURLまたはパス
        isPublic: isPublic,
        questionId: selectedQuestion?.id,
        questionTitle: selectedQuestion?.title,
        backgroundFilename: selectedPoster?.name // 背景画像ファイル名を追加
      };
      // 編集モード時はposterIdを追加
      if (location.state?.posterId) {
        payload.posterId = location.state.posterId;
      }
      const data = await createPoster(sessionId, payload);

      // 新規作成時のみ計測（編集は含めない）
      if (!payload.posterId) {
        trackPosterCreation(data.posterId);
      }

      // 自分のポスターキャッシュをクリア
      localStorage.removeItem(`myPosters_${sessionId}`);
      localStorage.removeItem(`myPosters_${sessionId}_timestamp`);
      
      navigate('/poster-detail', {
        state: {
          posterId: data.posterId,
          posterImageUrl: data.posterImageUrl,
          title: data.title,
          question: selectedQuestion?.title,
          fromPosterCreate: true
        }
      });
    } catch (error) {
      console.error('ポスター作成エラー:', error);
      alert('ポスター作成中にエラーが発生しました');
    } finally {
      setLoading(false); // Hide loading spinner
    }
  };

  // カルーセルの初期化とスクロール位置の固定
  useEffect(() => {
    const carousel = carouselRef.current;
    
    if (carousel) {
      // 強制的にスクロール位置を0に固定
      const resetScrollPosition = () => {
        carousel.scrollLeft = 0;
      };
      
      // 初期設定
      resetScrollPosition();
      
      // スクロールイベントを監視して自動スクロールを阻止
      const handleScroll = (e) => {
        // ボタンクリック以外でのスクロールを防ぐ
        if (!carousel.dataset.allowScroll) {
          e.preventDefault();
          resetScrollPosition();
        }
      };
      
      // 複数のイベントで監視
      carousel.addEventListener('scroll', handleScroll, { passive: false });
      
      // カルーセル初期化完了
      
      // クリーンアップ
      return () => {
        carousel.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  // postersが変更された時もスクロール位置をリセット
  useEffect(() => {
    const carousel = carouselRef.current;
    if (carousel) {
      carousel.scrollLeft = 0;
      // ポスターリスト更新、スクロール位置リセット
    }
  }, [posters]);

  return (
    <div className="container">
      {/* 課金モーダル */}
      {showPaymentModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            maxWidth: '400px',
            width: '90%'
          }}>
            <img 
              src="/img/cat.png" 
              alt="制限アイコン" 
              style={{ width: '60px', height: '60px'}}
            />
            <p style={{ fontSize: '16px', color: '#333', lineHeight: '1.6', marginBottom: '20px', letterSpacing: '1.0px', textAlign: 'left' }}>
              7個目以降のポスター作成に¥500円頂いています。
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <button 
                onClick={() => navigate('/poster-list')}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer'
                }}
              >
                ポスター一覧へ
              </button>
              <button 
                onClick={showPaymentUI}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  cursor: 'pointer'
                }}
              >
                ¥ 500
              </button>
            </div>
          </div>
        </div>
      )}

      {/* コンテンツ警告モーダル */}
      {showContentWarningModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: 'white',
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 4px 24px rgba(0, 0, 0, 0.1)',
            textAlign: 'center',
            maxWidth: '300px',
            width: '90%'
          }}>
            <img 
              src="/img/cat.png" 
              alt="警告アイコン" 
              style={{ width: '40px', height: '40px', marginBottom: '10px' }}
            />
            <p style={{ fontSize: '16px', color: '#666', lineHeight: '1.6', marginBottom: '30px', letterSpacing: '1.0px', textAlign: 'left' }}>
              {contentWarningData.message}
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              {contentWarningData.callback ? (
                // 続行オプションがある場合：キャンセル + 続行ボタン
                <>
                  <button 
                    onClick={handleContentWarningCancel}
                    style={{
                      padding: '12px 24px',
                      border: '1px solid #ccc',
                      borderRadius: '6px',
                      backgroundColor: 'white',
                      color: '#666',
                      cursor: 'pointer'
                    }}
                  >
                    キャンセル
                  </button>
                  <button 
                    onClick={handleContentWarningContinue}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    続行
                  </button>
                </>
              ) : (
                // 続行オプションがない場合：OKボタンのみ
                <button 
                  onClick={handleContentWarningCancel}
                  style={{
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    cursor: 'pointer'
                  }}
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner">
            <img src="/img/cat.png" alt="Loading..." className="loading-cat" />
            <p className="loading-text">ロード中・・</p>
          </div>
        </div>
      ) : (
        <div style={{ 
          filter: (showPaymentModal || showContentWarningModal) ? 'blur(3px)' : 'none',
          pointerEvents: (showPaymentModal || showContentWarningModal) ? 'none' : 'auto',
          transition: 'filter 0.3s ease'
        }}>
          {/* パンくずナビゲーション */}
          <nav className="create-breadcrumb">
            {location.state?.posterId ? (
              <>
                <button 
                  className="breadcrumb-link"
                  onClick={async () => {
                    if (!sessionId) {
                      alert('認証情報がありません。再ログインしてください。');
                      return;
                    }
                    try {
                      const { getPosterInfo } = await import('../firebase/api');
                      const info = await getPosterInfo(sessionId, location.state.posterId);
                      // ポスターの作者情報取得
                      navigate('/poster-detail', {
                        state: {
                          authorId: info.authorId,
                          posterId: info.posterId,
                          posterImageUrl: info.imageUrl,
                          question: info.question,
                          authorIconUrl: info.authorIconUrl,
                          authorName: info.authorName
                        }
                      });
                    } catch (err) {
                      alert('ポスター情報の取得に失敗しました');
                    }
                  }}
                >
                  ポスター詳細
                </button>
                <span className="breadcrumb-separator"> &gt; </span>
                <span className="breadcrumb-current">ポスター編集</span>
              </>
            ) : (
              <>
                <button 
                  className="breadcrumb-link"
                  onClick={() => navigate('/top')}
                >
                  ホーム
                </button>
                <span className="breadcrumb-separator"> &gt; </span>
                <span className="breadcrumb-current">ポスター作成</span>
              </>
            )}
          </nav>

          {/* ヘッダー */}
          <div className="top-header">
            <div className="user-info">
              <img src={userProfile?.iconURL || userProfile?.photoURL || '/img/icon.png'} alt="プロフィール画像" title="プロフィール編集" onClick={handleEditProfile} style={{cursor: 'pointer'}} />
              <span className="user-name">{userProfile?.displayName || userProfile?.nickname || 'ユーザー'} さん</span>
              {userProfile?.world && <span className="user-world">{userProfile.world}</span>}
            </div>
            <div className="header-icons">
              <img src="/img/graph.png" alt="みんなのサマリー" title="みんなのサマリー" className="header-icon" onClick={() => navigate('/')} style={{cursor: 'pointer'}} />
              <img src="/img/logout.png" alt="ログアウト" title="ログアウト" className="header-icon" onClick={() => { if (window.confirm('ログアウトしますか？')) logout(); }} style={{cursor: 'pointer', marginLeft: '12px'}} />
            </div>
          </div>

          {/* 編集モード時はポスター画像を表示（width: 100px） */}
          {editPosterInfo?.imageUrl && (
            <div style={{ textAlign: 'left', marginBottom: '12px', marginLeft: '12px' }}>
              <img src={editPosterInfo.imageUrl} alt="編集対象ポスター" style={{ width: '100px', borderRadius: '8px' }} />
            </div>
          )}
          {/* 選択された質問（titleがある時のみ表示） */}
          {selectedQuestion?.title && (
            <ul className="question-list">
              <li>
                <img src="/img/cat-purple.png" alt="質問アイコン" />
                <span className="question-text">{selectedQuestion.title}</span>
              </li>
            </ul>
          )}

          {/* 1. ポスター背景を選択 */}
          <div className="create-form-section">
            <label className="create-process">ポスター背景を選択</label>
            <div className="poster-select">
              <button className="nav-btn prev-btn" onClick={() => scrollCarousel('prev')}>◀</button>
              <div className="poster-carousel" id="posterCarousel" ref={carouselRef}>
                {posters.map((poster) => (
                  <img
                    key={poster.id}
                    src={poster.path}
                    alt={poster.name}
                    className={selectedPoster?.id === poster.id ? 'selected' : ''}
                    onClick={() => handlePosterSelect(poster)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </div>
              <button className="nav-btn next-btn" onClick={() => scrollCarousel('next')}>▶</button>
            </div>
          </div>

          {/* 2. 文字を入れる */}
          <div className="create-form-section">
            <label className="create-process">文字を入れる</label>
            <input 
              type="text" 
              placeholder="1行目：15文字まで"
              value={textLines[0]}
              maxLength={15}
              onChange={(e) => {
                const newLines = [...textLines];
                newLines[0] = e.target.value;
                setTextLines(newLines);
              }}
            />
            <input 
              type="text" 
              placeholder="2行目：15文字まで"
              value={textLines[1]}
              maxLength={15}
              onChange={(e) => {
                const newLines = [...textLines];
                newLines[1] = e.target.value;
                setTextLines(newLines);
              }}
            />
            <input 
              type="text" 
              placeholder="3行目：15文字まで" 
              value={textLines[2]}
              maxLength={15}
              onChange={(e) => {
                const newLines = [...textLines];
                newLines[2] = e.target.value;
                setTextLines(newLines);
              }}
            />
          </div>

          {/* 3. フォントを選択 */}
          <div className="create-form-section">
            <label className="create-process">フォントを選択</label>
            <div className="font-select-container">
              <div className="font-options">
                {availableFonts.map((font) => (
                  <div
                    key={font.id}
                    className={`font-option ${selectedFont === font.id ? 'selected' : ''}`}
                    onClick={() => handleFontSelect(font)}
                    style={{ fontFamily: font.family }}
                  >
                    {font.name}
                  </div>
                ))}
              </div>
              <div 
                className="font-sample" 
                style={{ fontFamily: getSelectedFontDetails().family }}
              >
                あいうえお
              </div>
            </div>
          </div>

          {/* 4. 公開設定 */}
          <div className="create-form-section">
            <label className="create-process">公開設定</label>
            <label className="create-toggle">
              <input 
                type="checkbox" 
                id="toggle-public"
                checked={isPublic}
                onChange={handleToggleChange}
              />
              <div className="create-slider"></div>
              <div className="create-label-text" id="toggle-label">{isPublic ? "on" : "off"}</div>
            </label>
          </div>

          {/* イメージを見るボタン */}
          <button className="create-btn-primary" onClick={handleGeneratePreview} disabled={isGenerating}>
            {isGenerating ? '生成中...' : 'イメージを見る'}
          </button>

          {/* 画像プレビューと決定ボタン群 */}
          {showPreviews && (
            <div className="poster-preview-container">
              {generatedPreviews.map((preview, index) => (
                <div key={index} className="poster-preview">
                  <img src={preview.imageUrl || "/img/p1.png"} alt={`preview${index + 1}`} />
                  <button className="select-btn" onClick={() => handleFinalSelect(preview.id)}>決定</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PosterCreate;
